import { Link } from 'react-router-dom';

// Components
import Picto from '../../atoms/Picto/Picto';
import { Skeleton } from '../../atoms/Skeleton/Skeleton';
import NavigationMenuPopover from '../NavigationMenuPopover/NavigationMenuPopover';

// Domain
import useBreadcrumb from '../../../domain/breadcrumb/useBreadcrumb';
import { getMenuPopoverView, hasMenuChildren } from '../../../domain/menu/menuNavigationUseCase';

// Utils & misc
import cn from '../../../utils/cn';

// Styles
import styles from './BreadCrumb.module.css';

/**
 * BreadCrumb component - Links of the current path; an item with a sub-menu opens it in a popover on click.
 * @param {object} params
 * @param {ApiManagerBreadcrumb[]} params.items - List of items to display in the breadcrumb
 * @param {string} [params.className] - Additional CSS classes
 * @param {ApiPageTemplate} [params.template] - Template of the breadcrumb
 * @param {boolean} [params.isLoading=false] - Whether the breadcrumb is loading
 * @returns {JSX.Element | null} BreadCrumb component
 */
function BreadCrumb({
  items, className, template, isLoading = false,
}) {
  const {
    visibleItems, openItemId, toggleItem, closePopover, navigateToItemId,
  } = useBreadcrumb(items);

  if (isLoading) {
    return (
      <section
        className={cn([
          styles.section,
          ...(template ? [styles[template]] : []),
          ...(className ? [className] : []),
        ])}
      >
        <ol className={cn([styles.list])} itemScope itemType="http://schema.org/BreadcrumbList">
          <li className={styles.item}>
            <Skeleton theme="table" shape="rectangle" width="150px" height="20px" />
          </li>
          <li className={styles.item}>
            <Skeleton theme="table" shape="rectangle" width="100px" height="20px" />
          </li>
        </ol>
      </section>
    );
  }

  /**
   * If no items are provided, return null
   */
  if (!items || items.length === 0) {
    return null;
  }

  /**
   * @param {ApiManagerBreadcrumb} listItem
   * @param {boolean} isLast
   * @returns {React.ReactNode}
   */
  const renderItemContent = (listItem, isLast) => {
    if (hasMenuChildren(listItem)) {
      const isOpen = openItemId === listItem.id;
      return (
        <NavigationMenuPopover
          isOpen={isOpen}
          onOpenChange={(open) => {
            if (!open) closePopover();
          }}
          title={listItem.label}
          description={listItem.description}
          settingsDescription={listItem.settingsDescription}
          view={getMenuPopoverView(isOpen ? listItem : null)}
          onLinkClick={navigateToItemId}
          placement="bottom-start"
          offsetX={0}
          triggerClassName={styles.trigger}
        >
          <button
            type="button"
            className={styles.link}
            aria-expanded={isOpen}
            onClick={() => toggleItem(listItem.id)}
          >
            <span itemProp="name">{listItem.label}</span>
          </button>
        </NavigationMenuPopover>
      );
    }

    if (isLast || !listItem.path) return <span itemProp="name">{listItem.label}</span>;

    return (
      <Link className={styles.link} itemProp="item" to={listItem.path}>
        <span itemProp="name">{listItem.label}</span>
      </Link>
    );
  };

  return (
    <section
      className={cn([
        styles.section,
        ...(template ? [styles[template]] : []),
        ...(className ? [className] : []),
      ])}
    >
      <ol className={cn([styles.list])} itemScope itemType="http://schema.org/BreadcrumbList">
        {visibleItems.map((listItem, index) => (
          <li
            key={listItem.id}
            itemProp="itemListElement"
            itemScope
            itemType="http://schema.org/ListItem"
            className={styles.item}
          >
            {renderItemContent(listItem, index === items.length - 1)}

            <meta itemProp="position" content={index.toString()} />

            {index < items.length - 1 ? (
              <Picto
                icon="arrow-right"
                className={styles.icon}
                aria-hidden="true"
              />
            ) : null}
          </li>
        ))}
      </ol>
    </section>
  );
}

export default BreadCrumb;
